import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const Appraisals = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">How appraisals work</Typography>
      <Typography>
        AmbuNet appraisals run in stages: the staff member confirms or updates
        their <strong>occupational health screening</strong>, completes a
        short <strong>pre-appraisal</strong>, then their{' '}
        <strong>self-review</strong>, and finally a reviewer completes the HR
        review and agrees objectives. Each appraisal record tracks its status
        through to <strong>Completed</strong>.
      </Typography>
      <Alert severity="info" variant="outlined">
        Staff complete their own appraisal steps from{' '}
        <DocLink url="/my-appraisals" text="My Appraisals" />. Completing the
        HR review requires the <strong>Personnel</strong>,{' '}
        <strong>Manager</strong>, or <strong>Supervisor</strong> team; site
        administrators always have access.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Turning appraisals on</Typography>
      <Typography>
        Appraisals are enabled by default and configured from{' '}
        <DocLink
          url="/site-configuration/appraisals"
          text="Configuration > Employees > Appraisals"
        />
        . There you choose a scheduling mode and can customise the questions
        asked in the staff-facing steps. In <strong>Automatic</strong> mode you
        set how often staff are appraised (every 6 months, annually, or every 2
        years) and when the first appraisal falls — from immediately on
        starting up to 2 years in. In <strong>Individual</strong> mode each
        employee's next appraisal date is set by hand on the{' '}
        <strong>Appraisals</strong> tab of their profile (the First Appraisal
        setting is not used); when an appraisal completes, the next date is
        filled in automatically from the appraisal frequency and can be
        adjusted per employee at any time.
      </Typography>
      <Typography>
        Once enabled, a daily scheduler automatically creates an appraisal
        record when a staff member is due — based on their last completed
        appraisal in automatic mode, or their individually set date. The{' '}
        <strong>Reminder Lead Time</strong> setting controls how many weeks
        before the due date the appraisal is created and the employee
        reminded, so staff get a head start on their self-review. A{' '}
        <strong>Run Scheduler Now</strong> button on the settings page creates
        any due records immediately rather than waiting for the overnight run.
      </Typography>
      <Alert severity="info" variant="outlined">
        If appraisals are disabled, My Appraisals tells staff so, and
        previously submitted appraisals remain viewable.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The staff member's part</Typography>
      <Typography>
        From <DocLink url="/my-appraisals" text="My Appraisals" />, staff open
        their current appraisal and work through the steps in order. First,
        their{' '}
        <DocLink
          docsPage
          url="/employees-hr/occupational-health"
          text="occupational health"
        />{' '}
        step shows a summary of their last screening — they confirm nothing
        has changed (which records a fresh dated copy) or go to the full form
        to update their details. Then they complete the pre-appraisal and the
        self-review. Once the staff steps are complete, the appraisal moves to{' '}
        <strong>Awaiting HR Review</strong>.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Reviewing an appraisal</Typography>
      <Typography>
        Reviewers pick up completed self-reviews from{' '}
        <DocLink url="/appraisals/pending-review" text="Open Appraisals" /> —
        also surfaced on the{' '}
        <DocLink
          docsPage
          url="/employees-hr/personnel-dashboard"
          text="Workforce Dashboard"
        />{' '}
        — or from the <strong>Appraisals</strong> tab on the employee's
        profile. The Open Appraisals page also lists appraisals still waiting
        for the staff member (shown first), so it's easy to see who needs
        chasing before anything reaches the review queue.
      </Typography>
      <Typography>
        The review screen brings the evidence to the reviewer: a snapshot of
        the employee's mandatory training position, plus any patient feedback
        and shout-outs received during the appraisal period, alongside the
        employee's own answers. The reviewer records their comments and
        completes the appraisal.
      </Typography>
    </Stack>
  </Stack>
)

export default Appraisals
